import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import swal from "sweetalert";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import PettyCashHeadModal from "./PettyCashModel";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";


import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";

const PETTY_CASH_SORT_FIELDS = {
  name: "Name",
  active: "Active",
  updateDatetime: "UpdateDatetime",
};

const PETTY_CASH_SORT_LABELS = {
  ID: "ID",
  [PETTY_CASH_SORT_FIELDS.name]: "Name",
  [PETTY_CASH_SORT_FIELDS.active]: "Active",
  [PETTY_CASH_SORT_FIELDS.updateDatetime]: "Last Updated",
};

const getHeadSortValue = (head, sortField) => {
  switch (sortField) {
    case "ID":
      return Number(head.id ?? head.ID ?? 0);
    case PETTY_CASH_SORT_FIELDS.name:
      return String(head.name ?? head.Name ?? "").toLowerCase();
    case PETTY_CASH_SORT_FIELDS.active:
      return (head.active ?? head.Active) ? 1 : 0;
    case PETTY_CASH_SORT_FIELDS.updateDatetime:
      return new Date(head.updateDatetime ?? head.UpdateDatetime ?? 0).getTime();
    default:
      return 0;
  }
};

const sortPettyCashHeads = (heads, sortBy, sortOrder) => {
  const field = sortBy?.value || "ID";
  const direction = sortOrder?.value === "ASC" ? 1 : -1;
  return [...heads].sort((a, b) => {
    const aVal = getHeadSortValue(a, field);
    const bVal = getHeadSortValue(b, field);
    if (aVal < bVal) return -1 * direction;
    if (aVal > bVal) return 1 * direction;
    return 0;
  });
};

const ManagePettyCashHeads = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "pettyCash";
  const defaultVisibleColumns = {
    active: true,
    updateDatetime: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [pettyCashHeads, setPettyCashHeads] = useState([]);
  const [statusConfirmHead, setStatusConfirmHead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pettyCashFetchSeqRef = useRef(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headToEdit, setHeadToEdit] = useState(null);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: PETTY_CASH_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const handleHeadSubmit = (submittedData, isUpdate) => {
    if (isUpdate) {
      setPettyCashHeads((prevHeads) =>
        prevHeads.map((head) =>
          head.id === submittedData.ID
            ? { ...head, ...submittedData, updateDatetime: new Date(submittedData.updateDateTime) }
            : head
        )
      );
    } else {
      setPettyCashHeads((prevHeads) => [
        ...prevHeads,
        {
          id: submittedData.ID || Date.now(),
          name: submittedData.name,
          active: submittedData.active,
          compID: submittedData.compID,
          updateDatetime: new Date(submittedData.updateDateTime),
        },
      ]);
    }
    fetchPettyCashHeads();
  };

  const DeletePettyCashHead = async (head) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${head.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_PETTY_CASH_HEAD, {
        params: {
          CompID,
          ID: head.id,
        },
      });

      if (response.data.status) {
        setPettyCashHeads((prevHeads) => {
          const next = prevHeads.filter((h) => h.id !== head.id);
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast("Petty Cash Head is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (head) => {
    setHeadToEdit(head);
    setIsModalOpen(true);
  };

  const confirmHeadStatusChange = async () => {
    if (!statusConfirmHead) return;

    const { id, active, name } = statusConfirmHead;
    const newActiveStatus = !active;

    setPettyCashHeads((prevHeads) =>
      prevHeads.map((h) => (h.id === id ? { ...h, active: newActiveStatus } : h))
    );
    setStatusConfirmHead(null);

    try {
      const response = await axiosInstance.post(APIs.UPDATE_PETTY_CASH_STATUS, {
        CompID,
        ID: id,
        Active: newActiveStatus,
      });

      if (response.data.status) {
        showSuccessToast(
          `"${name || "Petty Cash Head"}" is now ${newActiveStatus ? "Active" : "InActive"}.`
        );
        fetchPettyCashHeads();
      } else {
        setPettyCashHeads((prevHeads) =>
          prevHeads.map((h) => (h.id === id ? { ...h, active } : h))
        );
        showErrorToast(response.data.message);
      }
    } catch (err) {
      setPettyCashHeads((prevHeads) =>
        prevHeads.map((h) => (h.id === id ? { ...h, active } : h))
      );
      showErrorToast("Failed to update petty cash status.");
    }
  };

  // API returns full list; search is server-side; sort and pagination are client-side.
  const fetchPettyCashHeads = useCallback(async () => {
    const fetchSeq = ++pettyCashFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_PETTY_CASH_HEADS, {
        params: {
          CompID,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== pettyCashFetchSeqRef.current) return;

      const headsData = response.data.data || response.data;
      const heads = Array.isArray(headsData) ? headsData : [];
      setPettyCashHeads(heads);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, heads.length);
      }
    } catch (error) {
      if (fetchSeq !== pettyCashFetchSeqRef.current) return;
      setPettyCashHeads([]);
      showErrorToast("Failed to fetch petty cash heads.");
    } finally {
      if (fetchSeq === pettyCashFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [CompID, search]);

  const sortedPettyCashHeads = useMemo(
    () => sortPettyCashHeads(pettyCashHeads, sortBy, sortOrder),
    [pettyCashHeads, sortBy, sortOrder]
  );

  const totalRecords = sortedPettyCashHeads.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedPettyCashHeads = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return sortedPettyCashHeads.slice(start, start + size);
  }, [sortedPettyCashHeads, pageNumber, pageSize]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(pettyCashFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchPettyCashHeads(), [fetchPettyCashHeads]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";
  const isUnfilteredView = !hasFiltersApplied();

  const showEmptyCatalogState =
    initialLoadComplete && !loading && isUnfilteredView && catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No petty cash heads match your search "${search.trim()}". Try different keywords or clear filters to see all heads.`;
    }
    return "There are no petty cash heads to show right now. Add a new head or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || sortedPettyCashHeads.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No petty cash heads found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <ErrorBoundary>
      <div>
      <PettyCashHeadModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onHeadSubmit={handleHeadSubmit}
        compID={CompID}
        headToEdit={headToEdit}
      />

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Petty Cash Heads</li>
          </ol>
          <h1 className="page-header mb-0">Manage Petty Cash Heads</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={() => {
              setHeadToEdit(null);
              setIsModalOpen(true);
            }}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Petty Cash Head
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton
            onClick={() => {
              setHeadToEdit(null);
              setIsModalOpen(true);
            }}
            title="Add Petty Cash Head"
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Petty Cash Heads"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedPettyCashHeads.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{
                        "--modern-grid-sort-active": themeColor,
                      }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <SortableColumnHeader
                            sortField={PETTY_CASH_SORT_FIELDS.name}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleColumnSort}
                          >
                            Name
                          </SortableColumnHeader>
                          {visibleColumns.updateDatetime && (
                            <SortableColumnHeader
                              sortField={PETTY_CASH_SORT_FIELDS.updateDatetime}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Last Updated
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.active && (
                            <SortableColumnHeader
                              sortField={PETTY_CASH_SORT_FIELDS.active}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Status
                            </SortableColumnHeader>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPettyCashHeads.map((head) => (
                          <tr key={head.id}>
                            <td
                              className="align-middle"
                              onClick={() => handleEdit(head)}
                              style={{ cursor: "pointer" }}
                            >
                              {head.name}
                            </td>
                            {visibleColumns.updateDatetime && (
                              <td
                                className="align-middle text-center"
                                onClick={() => handleEdit(head)}
                                style={{ cursor: "pointer" }}
                              >
                                {new Date(head.updateDatetime).toLocaleString()}
                              </td>
                            )}
                            {visibleColumns.active && (
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <StatusBadge
                                  isActive={head.active}
                                  onClick={() => setStatusConfirmHead(head)}
                                />
                              </td>
                            )}
                            <td className="align-middle text-center">
                                <TableActionsMenu menuStyle={{ zIndex: 9999 }}>
                                  <TableActionsItem
                                    icon="fa fa-edit"
                                    onClick={() => handleEdit(head)}
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => DeletePettyCashHead(head)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change petty cash status?"
        subtitle={
          statusConfirmHead
            ? `Are you sure you want to set "${statusConfirmHead.name || "this head"}" to ${
                statusConfirmHead.active ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmHead(null)}
        isOpen={!!statusConfirmHead}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmHeadStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
      </div>
    </ErrorBoundary>
  );
};

export default ManagePettyCashHeads;
